import { Input } from "./input";
import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import React from "react";
import "./styles/controlled-textfield.css";

// Generic ControlledTextField for use with react-hook-form
export type ControlledTextFieldProps<T extends FieldValues> = Omit<
  UseControllerProps<T>,
  "control"
> &
  Omit<React.ComponentProps<"input">, "name" | "value" | "onChange" | "ref"> & {
    control: UseControllerProps<T>["control"];
    label?: string;
    type?: string;
    helperText?: string;
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
  };

export function ControlledTextField<T extends FieldValues>({
  control,
  name,
  defaultValue,
  rules,
  shouldUnregister,
  label,
  type = "text",
  helperText,
  startAdornment,
  endAdornment,
  ...inputProps
}: ControlledTextFieldProps<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({
    control: control!,
    name,
    ...(defaultValue !== undefined && { defaultValue }),
    ...(rules !== undefined && { rules }),
    ...(shouldUnregister !== undefined && { shouldUnregister }),
  });

  const hasStart = Boolean(startAdornment);
  const hasEnd = Boolean(endAdornment);

  const inputClassName = [
    hasStart ? "form-input--with-start" : "",
    hasEnd ? "form-input--with-end" : "",
    inputProps.className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={`form-field controlled-textfield ${
        error ? "form-field--error" : ""
      }`}
    >
      {label && (
        <span
          className={`form-field__label ${
            inputProps.required ? "form-field__label--required" : ""
          }`}
        >
          {label}
        </span>
      )}

      <div className="form-field__container controlled-textfield__container">
        {hasStart && (
          <div className="form-adornment form-adornment--start controlled-textfield__adornment controlled-textfield__adornment--start">
            {startAdornment}
          </div>
        )}

        <Input
          {...inputProps}
          {...field}
          type={type}
          className={inputClassName}
        />

        {hasEnd && (
          <div className="form-adornment form-adornment--end controlled-textfield__adornment controlled-textfield__adornment--end">
            {endAdornment}
          </div>
        )}
      </div>

      {helperText && <span className="form-field__helper">{helperText}</span>}
      {error && <span className="form-field__error">{error.message}</span>}
    </div>
  );
}
